import { ref, Ref } from '~/utils/nuxt3-migration'
import {
  base64ToFile,
  injectExif,
  readImageMetadata,
  resizeImage
} from '~/compositions/files/utils'
import { ResizedPhoto } from '~/models/photos/types'
import { useI18n } from '~/compositions/i18n'
import { useLogger } from '~/compositions/logger'
import { useAppConfirmationModal } from '~/compositions/app-confirmation-modal'
import { ConfirmationModalOptions } from '~/store/modules/shared/app-confirmation-modal/state'

export function useFile() {
  const { t } = useI18n()
  const logger = useLogger()
  const confirm = useAppConfirmationModal()

  let heicTo: any = null
  function fileToBase64(file: File): Promise<string> {
    return new Promise(resolve => {
      const fileReader = new FileReader()
      fileReader.readAsDataURL(file)
      fileReader.onload = (e: any) => {
        resolve(e.target?.result)
      }
    })
  }

  async function loadHeicTo() {
    try {
      heicTo = (await import('heic-to')).heicTo
    } catch (error) {
      logger.captureError(error)
    }
  }

  async function heicToJpeg(file: File): Promise<File> {
    try {
      if (!heicTo) {
        await loadHeicTo()
      }

      // get image as blob url
      const blobURL = URL.createObjectURL(file)

      // convert "fetch" the new blob url
      const blobRes = await fetch(blobURL)

      // convert response to blob
      const blob = await blobRes.blob()

      // convert to PNG - response is blob
      const conversionResult = await heicTo({
        blob
      })

      const fileToReturn = conversionResult as File
      // @ts-ignore
      fileToReturn.name = file.name
        .replace('heic', 'jpeg')
        .replace('HEIC', 'jpeg')
        .replace('heif', 'jpeg')
        .replace('HEIF', 'jpeg')

      return fileToReturn
    } catch (error) {
      logger.captureError(error)
      return file
    }
  }

  async function resizeAndReadImage({
    file,
    maxResizeWidth = 2100,
    maxResizeHeight = 1708,
    exactOnly = false, // throws error if image is not === the above values,
    minWidth = 0,
    minHeight = 0,
    loading = ref(false),
    backgroundColor = '#000000'
  }: {
    file: File
    maxResizeWidth?: number
    maxResizeHeight?: number
    exactOnly?: boolean
    minWidth?: number
    minHeight?: number
    loading?: Ref<boolean>
    backgroundColor?: string
  }): Promise<ResizedPhoto> {
    let fileToUse = file

    if (
      file.name.toLowerCase().includes('.heic') ||
      file.name.toLowerCase().includes('.heif')
    ) {
      if (heicTo === null) {
        loading.value = true
      }
      fileToUse = await heicToJpeg(file)
      if (heicTo) {
        loading.value = false
      }
    }

    const imageSource = await fileToBase64(fileToUse)
    const { orientation, make, exif } = await readImageMetadata(fileToUse)
    const image = new Image()
    return new Promise((resolve, reject) => {
      image.onload = (event: any) => {
        const eventWidth = event.target?.width
        const eventHeight = event.target?.height
        let width = event.target?.width
        let height = event.target?.height
        if (orientation > 4 && orientation < 9) {
          // intentionally flipping here
          width = eventHeight
          height = eventWidth
        }

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new TypeError('canvas not supported'))
          return
        }

        // throw error if image is not exactly at max values
        if (
          exactOnly &&
          width !== maxResizeWidth &&
          height !== maxResizeHeight
        ) {
          const error = t('image_not_exact_dimensions', {
            size: `${maxResizeWidth}x${maxResizeHeight}`
          }).toString()

          // @ts-ignore
          confirm.show({
            variant: 'info',
            title: t('image_dimensions'),
            text: error,
            size: 'md'
          } as ConfirmationModalOptions)

          throw new TypeError(error)
        }

        // Don't resize if it's small enough
        if (
          width <= maxResizeWidth &&
          height <= maxResizeHeight &&
          width >= minWidth &&
          height >= minHeight &&
          orientation < 0
        ) {
          canvas.width = image.width
          canvas.height = image.height
          ctx.fillStyle = backgroundColor
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height)

          let finalBase64 = canvas.toDataURL('image/jpeg')
          finalBase64 = injectExif(finalBase64, exif, true)

          resolve({
            photo: base64ToFile(finalBase64, fileToUse),
            thumb: finalBase64,
            width,
            height
          })
          return
        }

        // Attempt to resize
        try {
          const {
            file: resizedFile,
            thumb,
            width: resizeWidth,
            height: resizeHeight
          } = resizeImage({
            canvas,
            initWidth: width,
            initHeight: height,
            maxResizeWidth,
            maxResizeHeight,
            minWidth,
            minHeight,
            orientation,
            image,
            eventWidth,
            eventHeight,
            file: fileToUse,
            make,
            backgroundColor,
            exif
          })
          resolve({
            photo: resizedFile,
            thumb,
            width: resizeWidth,
            height: resizeHeight
          })
          return
        } finally {
          // If it fails fallback to drawing the image as is
          ctx.fillStyle = backgroundColor
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.drawImage(image, 0, 0, image.width, image.height)
          let finalBase64 = canvas.toDataURL('image/jpeg')
          finalBase64 = injectExif(finalBase64, exif, true)

          resolve({
            photo: base64ToFile(finalBase64, fileToUse),
            thumb: finalBase64,
            width,
            height
          })
        }
      }
      image.onerror = () => {
        reject(new TypeError('file_corrupted'))
      }

      image.src = imageSource
    })
  }

  function getCroppedImage(
    origFile: any,
    scrollX: number,
    scrollY: number,
    appliedWidth: number, // displayed image width in UI (with zoom)
    appliedHeight: number, // displayed image height in UI
    scaledCropperWidth: number, // the on-screen cropper window width
    scaledCropperHeight: number, // the on-screen cropper window height
    targetWidth: number, // final output width
    targetHeight: number, // final output height
    bgColor: string
  ): Promise<any> {
    const file = origFile.file
    let fileToUse = file

    // Convert HEIC if needed
    if (
      file.name.toLowerCase().includes('.heic') ||
      file.name.toLowerCase().includes('.heif')
    ) {
      fileToUse = heicToJpeg(file)
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = e => {
        if (fileToUse.type.match('image.*') && e.target) {
          const origImg = new Image()
          origImg.onload = event => {
            // @ts-ignore
            const imgWidth = event.target.width
            // @ts-ignore
            const imgHeight = event.target.height

            const canvas = document.createElement('canvas')
            canvas.width = targetWidth
            canvas.height = targetHeight
            canvas.style.opacity = '0'

            try {
              const ctx = canvas.getContext('2d')
              if (!ctx) throw new Error('Canvas context not supported')

              ctx.fillStyle = bgColor
              ctx.fillRect(0, 0, canvas.width, canvas.height)

              const ratioX = targetWidth / scaledCropperWidth
              const ratioY = targetHeight / scaledCropperHeight

              ctx.save()
              ctx.scale(ratioX, ratioY)

              const scaleX = appliedWidth / imgWidth
              const scaleY = appliedHeight / imgHeight

              ctx.translate(
                scaledCropperWidth / 2 + scrollX,
                scaledCropperHeight / 2 + scrollY
              )
              ctx.scale(scaleX, scaleY)
              ctx.drawImage(origImg, -imgWidth / 2, -imgHeight / 2)
              ctx.restore()

              const resultSrc = canvas.toDataURL('image/jpeg', 1)
              const resultFile = base64ToFile(resultSrc, fileToUse)

              resolve({ file: resultFile, src: resultSrc })
            } catch (e) {
              logger.captureError(new TypeError(`Error cropping`))
              logger.captureError(e)
              reject(e)
            }
          }
          origImg.src = e.target.result as string
        } else {
          logger.captureError(new TypeError(`File not an image`))
          reject(new TypeError(`File not an image`))
        }
      }

      reader.onerror = reject
      reader.readAsDataURL(fileToUse)
    })
  }

  function getDominantColorsFromImage(
    imgEl: HTMLImageElement,
    maxColors = 5
  ): string[] {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return []

    const width = (canvas.width = imgEl.naturalWidth || imgEl.width)
    const height = (canvas.height = imgEl.naturalHeight || imgEl.height)

    context.drawImage(imgEl, 0, 0, width, height)

    const { data } = context.getImageData(0, 0, width, height)

    const colorCounts = new Map<string, number>()

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i]
      const g = data[i + 1]
      const b = data[i + 2]
      const a = data[i + 3]

      if (a < 128) continue // skip fully/mostly transparent

      const hex = rgbToHex(r, g, b)
      colorCounts.set(hex, (colorCounts.get(hex) || 0) + 1)
    }

    const sorted = [...colorCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([hex]) => hex)

    return sorted.slice(0, maxColors)

    function rgbToHex(r: number, g: number, b: number) {
      return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')
    }
  }

  function adjustColorsForBackground(
    colors: string[],
    threshold = 4.5
  ): string[] {
    return colors.map(hex => {
      const [r, g, b] = hexToRgb(hex)
      const contrastToBlack = getContrast(r, g, b, 0, 0, 0)
      const contrastToWhite = getContrast(r, g, b, 255, 255, 255)

      if (contrastToBlack < threshold) {
        return rgbToHex(clamp(r + 40), clamp(g + 40), clamp(b + 40))
      }
      if (contrastToWhite < threshold) {
        return rgbToHex(clamp(r - 40), clamp(g - 40), clamp(b - 40))
      }

      return hex
    })
  }

  function getContrast(
    r1: number,
    g1: number,
    b1: number,
    r2: number,
    g2: number,
    b2: number
  ) {
    const luminance = (r: number, g: number, b: number) => {
      const toLinear = (c: number) => {
        c /= 255
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
      }
      return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
    }

    const L1 = luminance(r1, g1, b1)
    const L2 = luminance(r2, g2, b2)
    return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05)
  }

  function clamp(val: number): number {
    return Math.max(0, Math.min(255, val))
  }

  function rgbToHex(r: number, g: number, b: number) {
    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')
  }

  function hexToRgb(hex: string): [number, number, number] {
    const bigint = parseInt(hex.slice(1), 16)
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255]
  }

  return {
    fileToBase64,
    resizeAndReadImage,
    heicToJpeg,
    getDominantColorsFromImage,
    adjustColorsForBackground,
    getCroppedImage,
    loadHeicTo
  }
}
